import { fetchProductThumbnailUrl } from '../api/product';

type CacheEntry = {
  url: string;
  version: string;
};

const cache = new Map<number, CacheEntry>();
const inflight = new Map<number, Promise<string>>();

export function productImageVersion(updatedAt?: string | null): string {
  return updatedAt?.trim() || '0';
}

export function peekProductThumbUrl(productId: number, version: string): string | null {
  const hit = cache.get(productId);
  if (!hit || hit.version !== version) return null;
  return hit.url;
}

export async function getProductThumbUrl(productId: number, version: string): Promise<string> {
  const hit = cache.get(productId);
  if (hit && hit.version === version) {
    return hit.url;
  }

  const pending = inflight.get(productId);
  if (pending) {
    return pending;
  }

  const request = (async () => {
    const url = await fetchProductThumbnailUrl(productId);
    const prev = cache.get(productId);
    if (prev && prev.url !== url) {
      window.URL.revokeObjectURL(prev.url);
    }
    cache.set(productId, { url, version });
    return url;
  })().finally(() => {
    inflight.delete(productId);
  });

  inflight.set(productId, request);
  return request;
}

/** 목록에 없는 제품 썸네일만 정리 (세션 캐시 유지) */
export function pruneProductThumbCache(keepIds: Iterable<number>) {
  const keep = new Set(keepIds);
  Array.from(cache.entries()).forEach(([productId, entry]) => {
    if (keep.has(productId)) return;
    window.URL.revokeObjectURL(entry.url);
    cache.delete(productId);
  });
}

export function invalidateProductThumb(productId: number) {
  const entry = cache.get(productId);
  if (entry) {
    window.URL.revokeObjectURL(entry.url);
    cache.delete(productId);
  }
  inflight.delete(productId);
}

export function clearProductThumbCache() {
  Array.from(cache.values()).forEach((entry) => {
    window.URL.revokeObjectURL(entry.url);
  });
  cache.clear();
  inflight.clear();
}
